import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Card,
  CopyButton,
  Group,
  PasswordInput,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconEye, IconEyeOff, IconRefresh } from '@tabler/icons-react';
import { truckingIntegrationService, type IntegrationView } from '@/api/services/TruckingIntegrationService';
import { INTEGRATION_QUERY_KEY, useIntegration } from '@/hooks/useIntegration';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { getApiMessage, getStatus, isNetworkError } from '@/api/errors';
import { ROUTES } from '@/routes/paths';
import { MONO_INPUT_STYLES } from '@/styles/theme';

const BASE_URL_RE = /^https?:\/\/.+/;

interface ServerFormValues {
  baseUrl: string;
  authToken: string;
}

/**
 * «Интеграция» перевозчика (§5.5a): подключение его сервера (baseUrl + токен) и ключ data1861_api_key.
 * Эндпоинты trucking-service требуют аттестацию — при 403 показываем призыв пройти аттестацию.
 * Документация «Администраторам»/«Разработчикам» — §5.5b.
 */
export default function CarrierIntegrationPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useIntegration();
  const [revealed, setRevealed] = useState(false);

  const forbidden = isError && getStatus(error) === 403;
  const connected = data?.tokenConfigured === true;

  const form = useForm<ServerFormValues>({
    mode: 'controlled',
    initialValues: { baseUrl: '', authToken: '' },
    validateInputOnBlur: true,
    validate: {
      baseUrl: (value) => (BASE_URL_RE.test(value.trim()) ? null : t('app.carrier.integration.errUrl')),
      authToken: (value) => (value.trim().length > 0 ? null : t('app.carrier.integration.errToken')),
    },
  });

  // Предупреждение об уходе с несохранёнными правками (адрес/токен). Вызываем до ранних return.
  useUnsavedChangesGuard(() => form.isDirty());

  // Подстановка baseUrl из загруженных данных + baseline для isDirty. Токен write-only — всегда пустой.
  // Не перезаписываем правки пользователя фоновым рефетчем: синхронизируем только когда форма не dirty.
  useEffect(() => {
    if (data && !form.isDirty()) {
      const values: ServerFormValues = { baseUrl: data.baseUrl ?? '', authToken: '' };
      form.setValues(values);
      form.resetDirty(values);
    }
    // Зависимость только от data; form стабилен в рамках жизни компонента.
  }, [data]);

  function notifyError(e: unknown): void {
    notifications.show({
      color: 'red',
      message: isNetworkError(e) ? t('auth.errors.generic') : (getApiMessage(e) ?? t('auth.errors.generic')),
    });
  }

  const save = useMutation({
    mutationFn: (values: ServerFormValues) =>
      truckingIntegrationService.saveIntegration({ baseUrl: values.baseUrl.trim(), authToken: values.authToken.trim() }),
    onSuccess: (view: IntegrationView) => {
      queryClient.setQueryData(INTEGRATION_QUERY_KEY, view);
      // Токен в форме не держим; baseline dirty — по сохранённому baseUrl.
      const values: ServerFormValues = { baseUrl: view.baseUrl ?? '', authToken: '' };
      form.setValues(values);
      form.resetDirty(values);
      notifications.show({ color: 'lime', message: t('app.carrier.integration.saved') });
    },
    onError: notifyError,
  });

  const rotate = useMutation({
    mutationFn: () => truckingIntegrationService.rotateApiKey(),
    onSuccess: (newKey: string) => {
      queryClient.setQueryData<IntegrationView>(INTEGRATION_QUERY_KEY, (prev) =>
        prev ? { ...prev, apiKey: newKey } : prev,
      );
      setRevealed(true); // показать новый ключ, чтобы пользователь успел скопировать
      notifications.show({ color: 'lime', message: t('app.carrier.integration.rotated') });
    },
    onError: notifyError,
  });

  function confirmRotate(): void {
    modals.openConfirmModal({
      title: t('app.carrier.integration.rotate'),
      children: <Text size="sm">{t('app.carrier.integration.rotateWarning')}</Text>,
      labels: { confirm: t('app.carrier.integration.rotate'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => rotate.mutate(),
    });
  }

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton h={180} />
        <Skeleton h={120} />
      </Stack>
    );
  }

  if (forbidden) {
    return (
      <Alert color="wheat" variant="light" title={t('app.carrier.integration.attestation.title')}>
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{t('app.carrier.integration.attestation.text')}</Text>
          <Anchor component={Link} to={ROUTES.APP_COMPANY_ACCREDITATION} size="sm" fw={500}>
            {t('app.carrier.integration.attestation.action')}
          </Anchor>
        </Stack>
      </Alert>
    );
  }

  if (isError) {
    return (
      <Alert color="red" variant="light">
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm">{t('auth.errors.generic')}</Text>
          <Button variant="subtle" size="compact-sm" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </Group>
      </Alert>
    );
  }

  const apiKey = data?.apiKey ?? null;
  const keyDisplay = apiKey ? (revealed ? apiKey : '•'.repeat(Math.min(apiKey.length, 32))) : '';

  return (
    <Stack gap="lg">
      {/* Секция «Сервер перевозчика» */}
      <Card withBorder padding="lg">
        <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
          <Stack gap="md">
            <Stack gap={4}>
              <Title order={4}>{t('app.carrier.integration.server.title')}</Title>
              <Text c="gray.7" size="sm">
                {t('app.carrier.integration.server.desc')}
              </Text>
            </Stack>

            <TextInput
              key={form.key('baseUrl')}
              label={t('app.carrier.integration.baseUrl')}
              placeholder="https://example.com"
              inputMode="url"
              styles={MONO_INPUT_STYLES}
              {...form.getInputProps('baseUrl')}
            />

            <PasswordInput
              key={form.key('authToken')}
              label={t('app.carrier.integration.token')}
              placeholder={connected ? t('app.carrier.integration.tokenSetPlaceholder') : undefined}
              description={connected ? t('app.carrier.integration.tokenSetHint') : t('app.carrier.integration.tokenHint')}
              autoComplete="off"
              {...form.getInputProps('authToken')}
            />

            <Button type="submit" w="fit-content" loading={save.isPending}>
              {t('app.carrier.integration.save')}
            </Button>
          </Stack>
        </form>
      </Card>

      {/* Секция «API-ключ data1861» — только после подключения (до него ключа нет, ротация вернула бы 422) */}
      {connected && apiKey ? (
        <Card withBorder padding="lg">
          <Stack gap="md">
            <Stack gap={4}>
              <Title order={4}>{t('app.carrier.integration.key.title')}</Title>
              <Text c="gray.7" size="sm">
                {t('app.carrier.integration.key.desc')}
              </Text>
            </Stack>

            <TextInput
              label={t('app.carrier.integration.key.label')}
              readOnly
              value={keyDisplay}
              styles={MONO_INPUT_STYLES}
              rightSectionWidth={76}
              rightSectionPointerEvents="all"
              rightSection={
                <Group gap={2} wrap="nowrap">
                  <Tooltip label={revealed ? t('auth.password.hide') : t('auth.password.show')}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => setRevealed((value) => !value)}
                      aria-label={revealed ? t('auth.password.hide') : t('auth.password.show')}
                    >
                      {revealed ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                    </ActionIcon>
                  </Tooltip>
                  <CopyButton value={apiKey} timeout={1500}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? t('app.profile.api.copied') : t('app.profile.api.copy')}>
                        <ActionIcon
                          variant="subtle"
                          color={copied ? 'lime' : 'gray'}
                          onClick={copy}
                          aria-label={t('app.profile.api.copy')}
                        >
                          {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              }
            />

            <Button
              leftSection={<IconRefresh size={18} />}
              w="fit-content"
              variant="default"
              loading={rotate.isPending}
              onClick={confirmRotate}
            >
              {t('app.carrier.integration.rotate')}
            </Button>
          </Stack>
        </Card>
      ) : null}

      {/* Документация по интеграции. «Для разработчиков» добавляется в §5.5b-2. */}
      <Card withBorder padding="lg">
        <Stack gap="xs" align="flex-start">
          <Title order={4}>{t('app.carrier.integration.docs.title')}</Title>
          <Anchor component={Link} to={ROUTES.APP_CARRIER_INTEGRATION_ADMIN} fw={500}>
            {t('app.carrier.integration.docs.adminLink')}
          </Anchor>
          <Anchor component={Link} to={ROUTES.APP_CARRIER_INTEGRATION_DEV} fw={500}>
            {t('app.carrier.integration.docs.devLink')}
          </Anchor>
        </Stack>
      </Card>
    </Stack>
  );
}
